import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Alert,
  Snackbar
} from '@mui/material';
import { Add, Delete as DeleteIcon, Edit as EditIcon, Refresh } from '@mui/icons-material';

import ModulePageHeader from '../common/ModulePageHeader';
import { academicAPI, reportCardAPI } from '../../services/api';
import ResponsiveRecordList, { EmptyRecordsState } from '../common/ResponsiveRecordList';
import TablePagination from '../common/TablePagination';

const INITIAL_FORM = {
  template_name: '',
  template_code: '',
  applicable_level_id: '',
  show_aggregate: true,
  show_total_aggregate: true,
  show_stream: true,
  show_class_position: true,
  show_stream_position: true,
  show_subject_rank: false,
  show_division: true,
  show_conduct: true,
  show_subject_comments: true,
  show_marks: true,
  show_grade: true,
  assessment_display_mode: 'weighted',
  rounding_mode: 'half_up',
  default_comment_tone: 'balanced',
  paper_size: 'A4',
  orientation: 'Portrait',
  promotion_threshold: '',
  probation_threshold: ''
};

const PAPER_SIZES = ['A4', 'Letter', 'Legal'];
const ORIENTATIONS = ['Portrait', 'Landscape'];
const ROUNDING_MODES = ['half_up', 'half_even', 'truncate'];
const COMMENT_TONES = ['balanced', 'strict', 'encouraging'];

const extractCollection = (r) => {
  const p = r?.data !== undefined ? r.data : r;
  if (Array.isArray(p)) return p;
  if (Array.isArray(p?.data)) return p.data;
  if (Array.isArray(p?.data?.data)) return p.data.data;
  return [];
};

const readError = (e, fallback = 'Operation failed') => e?.response?.data?.message || fallback;

export default function ReportCardTemplates() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [templates, setTemplates] = useState([]);
  const [levels, setLevels] = useState([]);
  const [form, setForm] = useState(INITIAL_FORM);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [tmplRes, levelRes] = await Promise.all([
        reportCardAPI.getReportCardTemplates(),
        academicAPI.getLevels()
      ]);
      setTemplates(extractCollection(tmplRes));
      setLevels(extractCollection(levelRes));
    } catch (e) {
      console.error(e);
      setSnackbar({ open: true, message: readError(e, 'Error fetching templates'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => {
    setForm(INITIAL_FORM);
    setEditing(null);
  };

  const currentLevelLabel = (levelId) => {
    const level = levels.find((l) => String(l.id) === String(levelId));
    return level?.name || 'All Levels';
  };

  const handleEdit = (template) => {
    setEditing(template);
    setForm({
      template_name: template.template_name || '',
      template_code: template.template_code || '',
      applicable_level_id: template.applicable_level_id ?? '',
      show_aggregate: template.show_aggregate ?? true,
      show_total_aggregate: template.show_total_aggregate ?? true,
      show_stream: template.show_stream ?? true,
      show_class_position: template.show_class_position ?? true,
      show_stream_position: template.show_stream_position ?? true,
      show_subject_rank: template.show_subject_rank ?? false,
      show_division: template.show_division ?? true,
      show_conduct: template.show_conduct ?? true,
      show_subject_comments: template.show_subject_comments ?? true,
      show_marks: template.show_marks ?? true,
      show_grade: template.show_grade ?? true,
      assessment_display_mode: template.assessment_display_mode || 'weighted',
      rounding_mode: template.rounding_mode || 'half_up',
      default_comment_tone: template.default_comment_tone || 'balanced',
      paper_size: template.paper_size || 'A4',
      orientation: template.orientation || 'Portrait',
      promotion_threshold: template.promotion_threshold ?? '',
      probation_threshold: template.probation_threshold ?? ''
    });
  };

  const handleDelete = async (template) => {
    if (!window.confirm(`Delete template "${template.template_name}"?`)) return;
    try {
      await reportCardAPI.deleteReportCardTemplate(template.id);
      await fetchData();
    } catch (e) {
      setSnackbar({ open: true, message: readError(e, 'Error deleting template'), severity: 'error' });
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form.template_name.trim() || !form.template_code.trim()) {
      setSnackbar({ open: true, message: 'Template name and code are required.', severity: 'error' });
      return;
    }

    setSaving(true);
    const payload = {
      ...form,
      applicable_level_id: form.applicable_level_id === '' ? null : Number(form.applicable_level_id),
      promotion_threshold: form.promotion_threshold === '' ? null : Number(form.promotion_threshold),
      probation_threshold: form.probation_threshold === '' ? null : Number(form.probation_threshold)
    };

    try {
      if (editing) {
        await reportCardAPI.updateReportCardTemplate(editing.id, payload);
      } else {
        await reportCardAPI.createReportCardTemplate(payload);
      }
      setSnackbar({ open: true, message: editing ? 'Template updated successfully!' : 'Template created successfully!', severity: 'success' });
      resetForm();
      await fetchData();
    } catch (e) {
      setSnackbar({ open: true, message: readError(e, 'Error saving template'), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const pagedTemplates = useMemo(() => {
    const start = (page - 1) * perPage;
    return templates.slice(start, start + perPage);
  }, [templates, page, perPage]);

  const pagination = {
    current_page: page,
    last_page: Math.max(1, Math.ceil(templates.length / perPage)),
    per_page: perPage,
    total: templates.length
  };

  return (
    <Box>
      <ModulePageHeader
        title="Report Card Templates"
        subtitle="Manage report card layouts and visibility settings"
        actions={[
          <Button key="refresh" variant="outlined" startIcon={<Refresh />} onClick={fetchData} disabled={loading} fullWidth={isMobile}>
            Refresh
          </Button>
        ]}
      />

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>{editing ? 'Edit Template' : 'Create Template'}</Typography>
          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Template Name"
                  value={form.template_name}
                  onChange={(e) => setForm((p) => ({ ...p, template_name: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Template Code"
                  value={form.template_code}
                  onChange={(e) => setForm((p) => ({ ...p, template_code: e.target.value.toUpperCase().trim() }))}
                  required
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Applicable Level</InputLabel>
                  <Select
                    label="Applicable Level"
                    value={form.applicable_level_id}
                    onChange={(e) => setForm((p) => ({ ...p, applicable_level_id: e.target.value }))}
                  >
                    <MenuItem value="">All Levels</MenuItem>
                    {levels.map((lvl) => (
                      <MenuItem key={lvl.id} value={lvl.id}>{lvl.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Paper Size</InputLabel>
                  <Select
                    label="Paper Size"
                    value={form.paper_size}
                    onChange={(e) => setForm((p) => ({ ...p, paper_size: e.target.value }))}
                  >
                    {PAPER_SIZES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Orientation</InputLabel>
                  <Select
                    label="Orientation"
                    value={form.orientation}
                    onChange={(e) => setForm((p) => ({ ...p, orientation: e.target.value }))}
                  >
                    {ORIENTATIONS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Rounding Mode</InputLabel>
                  <Select
                    label="Rounding Mode"
                    value={form.rounding_mode}
                    onChange={(e) => setForm((p) => ({ ...p, rounding_mode: e.target.value }))}
                  >
                    {ROUNDING_MODES.map((s) => <MenuItem key={s} value={s}>{s.replace('_', ' ')}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Comment Tone</InputLabel>
                  <Select
                    label="Comment Tone"
                    value={form.default_comment_tone}
                    onChange={(e) => setForm((p) => ({ ...p, default_comment_tone: e.target.value }))}
                  >
                    {COMMENT_TONES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Stack direction={isMobile ? 'column' : 'row'} spacing={2}>
                  <FormControlLabel
                    control={<Switch checked={form.show_aggregate} onChange={(e) => setForm((p) => ({ ...p, show_aggregate: e.target.checked }))} />}
                    label="Show Aggregate (per subject)"
                  />
                  <FormControlLabel
                    control={<Switch checked={form.show_total_aggregate} onChange={(e) => setForm((p) => ({ ...p, show_total_aggregate: e.target.checked }))} />}
                    label="Show Total Aggregate"
                  />
                  <FormControlLabel
                    control={<Switch checked={form.show_stream} onChange={(e) => setForm((p) => ({ ...p, show_stream: e.target.checked }))} />}
                    label="Show Stream"
                  />
                  <FormControlLabel
                    control={<Switch checked={form.show_class_position} onChange={(e) => setForm((p) => ({ ...p, show_class_position: e.target.checked }))} />}
                    label="Show Class Position"
                  />
                  <FormControlLabel
                    control={<Switch checked={form.show_stream_position} onChange={(e) => setForm((p) => ({ ...p, show_stream_position: e.target.checked }))} />}
                    label="Show Stream Position"
                  />
                  <FormControlLabel
                    control={<Switch checked={form.show_subject_rank} onChange={(e) => setForm((p) => ({ ...p, show_subject_rank: e.target.checked }))} />}
                    label="Show Subject Rank"
                  />
                  <FormControlLabel
                    control={<Switch checked={form.show_division} onChange={(e) => setForm((p) => ({ ...p, show_division: e.target.checked }))} />}
                    label="Show Division"
                  />
                  <FormControlLabel
                    control={<Switch checked={form.show_conduct} onChange={(e) => setForm((p) => ({ ...p, show_conduct: e.target.checked }))} />}
                    label="Show Conduct"
                  />
                  <FormControlLabel
                    control={<Switch checked={form.show_subject_comments} onChange={(e) => setForm((p) => ({ ...p, show_subject_comments: e.target.checked }))} />}
                    label="Show Subject Comments"
                  />
                  <FormControlLabel
                    control={<Switch checked={form.show_marks} onChange={(e) => setForm((p) => ({ ...p, show_marks: e.target.checked }))} />}
                    label="Show Marks"
                  />
                  <FormControlLabel
                    control={<Switch checked={form.show_grade} onChange={(e) => setForm((p) => ({ ...p, show_grade: e.target.checked }))} />}
                    label="Show Grade"
                  />
                </Stack>
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField
                  fullWidth size="small" type="number" label="Promote Threshold (%)"
                  value={form.promotion_threshold}
                  onChange={(e) => setForm((p) => ({ ...p, promotion_threshold: e.target.value }))}
                  helperText="≥ this % → Promoted"
                  inputProps={{ min: 0, max: 100, step: 0.5 }}
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField
                  fullWidth size="small" type="number" label="Probation Threshold (%)"
                  value={form.probation_threshold}
                  onChange={(e) => setForm((p) => ({ ...p, probation_threshold: e.target.value }))}
                  helperText="≥ this % → On Probation"
                  inputProps={{ min: 0, max: 100, step: 0.5 }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Assessment Display</InputLabel>
                  <Select
                    label="Assessment Display"
                    value={form.assessment_display_mode}
                    onChange={(e) => setForm((p) => ({ ...p, assessment_display_mode: e.target.value }))}
                  >
                    <MenuItem value="weighted">Weighted % (contribution to total)</MenuItem>
                    <MenuItem value="raw">Raw Marks (actual score)</MenuItem>
                    <MenuItem value="progress">Progress Layout (BOT / MOT / EOT per subject)</MenuItem>
                    <MenuItem value="secondary">Secondary (No totals + Projects table)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Stack direction="row" spacing={2}>
                  <Button type="submit" variant="contained" startIcon={<Add />} disabled={saving}>
                    {editing ? 'Update Template' : 'Create Template'}
                  </Button>
                  {editing && (
                    <Button variant="outlined" onClick={resetForm} disabled={saving}>Cancel</Button>
                  )}
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {loading ? (
            <Typography variant="body2" color="text.secondary">Loading templates...</Typography>
          ) : templates.length === 0 ? (
            <EmptyRecordsState message="No report card templates found." />
          ) : isMobile ? (
            <>
              <ResponsiveRecordList
                items={pagedTemplates}
                renderItem={(t) => (
                  <Stack spacing={0.75}>
                    <Typography fontWeight={700}>{t.template_name}</Typography>
                    <Typography variant="body2" color="text.secondary">Code: {t.template_code}</Typography>
                    <Typography variant="body2" color="text.secondary">Level: {currentLevelLabel(t.applicable_level_id)}</Typography>
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                      {t.show_aggregate && <Chip label="Aggregate" size="small" />}
                      {t.show_total_aggregate && <Chip label="Total Agg" size="small" />}
                      {t.show_class_position && <Chip label="Class Pos" size="small" />}
                      {t.show_stream_position && <Chip label="Stream Pos" size="small" />}
                      {t.show_subject_rank && <Chip label="Rank" size="small" />}
                      {t.show_division && <Chip label="Division" size="small" />}
                      {t.show_conduct && <Chip label="Conduct" size="small" />}
                      {t.show_subject_comments && <Chip label="Comments" size="small" />}
                      {t.show_marks && <Chip label="Marks" size="small" />}
                      {t.show_grade && <Chip label="Grade" size="small" />}
                      <Chip label={t.assessment_display_mode === 'raw' ? 'Raw Marks' : t.assessment_display_mode === 'progress' ? 'Progress Layout' : t.assessment_display_mode === 'secondary' ? 'Secondary' : 'Weighted %'} size="small" />
                    </Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">{t.paper_size} {t.orientation}</Typography>
                      <Box>
                        <IconButton size="small" color="primary" onClick={() => handleEdit(t)}><EditIcon /></IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDelete(t)}><DeleteIcon /></IconButton>
                      </Box>
                    </Box>
                  </Stack>
                )}
              />
              <TablePagination pagination={pagination} onPageChange={setPage} onPageSizeChange={(s) => { setPerPage(s); setPage(1); }} isMobile />
            </>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Code</TableCell>
                      <TableCell>Level</TableCell>
                      <TableCell>Options</TableCell>
                      <TableCell>Paper</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pagedTemplates.map((t) => (
                      <TableRow key={t.id} hover>
                        <TableCell>{t.template_name}</TableCell>
                        <TableCell><Chip label={t.template_code} size="small" /></TableCell>
                        <TableCell>{currentLevelLabel(t.applicable_level_id)}</TableCell>
                        <TableCell>
                          {t.show_aggregate && <Chip label="Aggregate" size="small" sx={{ mr: 0.5 }} />}
                          {t.show_total_aggregate && <Chip label="Total Agg" size="small" sx={{ mr: 0.5 }} />}
                          {t.show_class_position && <Chip label="Class Pos" size="small" sx={{ mr: 0.5 }} />}
                          {t.show_stream_position && <Chip label="Stream Pos" size="small" sx={{ mr: 0.5 }} />}
                          {t.show_subject_rank && <Chip label="Rank" size="small" sx={{ mr: 0.5 }} />}
                          {t.show_division && <Chip label="Division" size="small" sx={{ mr: 0.5 }} />}
                          {t.show_conduct && <Chip label="Conduct" size="small" sx={{ mr: 0.5 }} />}
                          {t.show_subject_comments && <Chip label="Comments" size="small" sx={{ mr: 0.5 }} />}
                          {t.show_marks && <Chip label="Marks" size="small" sx={{ mr: 0.5 }} />}
                          {t.show_grade && <Chip label="Grade" size="small" sx={{ mr: 0.5 }} />}
                          <Chip label={t.assessment_display_mode === 'raw' ? 'Raw Marks' : t.assessment_display_mode === 'progress' ? 'Progress Layout' : t.assessment_display_mode === 'secondary' ? 'Secondary' : 'Weighted %'} size="small" sx={{ mr: 0.5 }} />
                        </TableCell>
                        <TableCell>{t.paper_size} {t.orientation}</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" color="primary" onClick={() => handleEdit(t)}><EditIcon fontSize="small" /></IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDelete(t)}><DeleteIcon fontSize="small" /></IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination pagination={pagination} onPageChange={setPage} onPageSizeChange={(s) => { setPerPage(s); setPage(1); }} isMobile={isMobile} />
            </>
          )}
        </CardContent>
      </Card>
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
